"use client";

import { RJSFSchema, UiSchema } from "@rjsf/utils";
import type { UIMessage } from "ai";
import { useCallback, useEffect, useRef, useState } from "react";
import { StepperJsonForm } from "../widgets/StepperJsonForm";

type ActiveFormLayerProps = {
  messages: UIMessage[];
  addToolOutput: (options: { tool: string; toolCallId: string; output: unknown }) => Promise<void>;
  onActiveFormChange: (
    hasActiveForm: boolean,
    onTextSubmit?: (text: string) => void,
    fieldContext?: { placeholder?: string; fieldType?: string },
  ) => void;
};

type ActiveForm = {
  toolCallId: string;
  schema: RJSFSchema;
  uiSchema?: UiSchema;
};

type FieldContext = {
  placeholder?: string;
  fieldType?: string;
};

type FieldType = "string" | "number" | "integer" | "boolean" | "array" | "object";

export function ActiveFormLayer({
  messages,
  addToolOutput,
  onActiveFormChange,
}: ActiveFormLayerProps) {
  const [activeForm, setActiveForm] = useState<ActiveForm | null>(null);
  const [currentFieldIndex, setCurrentFieldIndex] = useState(0);
  const [fieldAnswers, setFieldAnswers] = useState<Record<string, unknown>>({});
  const [textInputError, setTextInputError] = useState<string | null>(null);
  const fieldKeysRef = useRef<string[]>([]);
  const currentFieldDataRef = useRef<{ fieldName: string; value: unknown } | null>(null);
  const handleTextSubmitRef = useRef<((text: string) => void) | null>(null);

  // Extract active multi-step form from messages
  useEffect(() => {
    // Find last pending show_form with x-multiStep: true
    const lastMessage = [...messages].reverse().find((m) => m.role === "assistant");
    if (!lastMessage) {
      if (activeForm) {
        setActiveForm(null);
        onActiveFormChange(false);
      }
      return;
    }

    const formPart = lastMessage.parts.find(
      (p) =>
        typeof p.type === "string" &&
        p.type === "tool-show_form" &&
        (p as { state?: string }).state === "input-available",
    );

    if (!formPart) {
      if (activeForm) {
        setActiveForm(null);
        onActiveFormChange(false);
      }
      return;
    }

    const toolPart = formPart as {
      toolCallId: string;
      input: Record<string, unknown>;
    };
    const input = toolPart.input;

    // Inline forms render directly in the chat message list, not in the stepper overlay
    if (input.inline) {
      if (activeForm) {
        setActiveForm(null);
        onActiveFormChange(false);
      }
      return;
    }

    const schema = input.schema as RJSFSchema;
    const uiSchema = input.uiSchema as UiSchema | undefined;

    // All non-inline JsonForms render in ActiveFormLayer (single or multi-field)

    // Extract field keys (ordered)
    const properties = schema.properties as Record<string, RJSFSchema> | undefined;
    if (!properties) {
      if (activeForm) {
        setActiveForm(null);
        onActiveFormChange(false);
      }
      return;
    }

    const fieldKeys = Object.keys(properties);
    if (fieldKeys.length === 0) {
      if (activeForm) {
        setActiveForm(null);
        onActiveFormChange(false);
      }
      return;
    }

    // Check if this is a new form (different toolCallId)
    if (!activeForm || activeForm.toolCallId !== toolPart.toolCallId) {
      setActiveForm({
        toolCallId: toolPart.toolCallId,
        schema,
        uiSchema,
      });
      setCurrentFieldIndex(0);
      setFieldAnswers({});
      setTextInputError(null);
      fieldKeysRef.current = fieldKeys;

      // Get placeholder for first field
      const firstFieldKey = fieldKeys[0];
      const firstFieldSchema = properties[firstFieldKey];
      const fieldContext = getFieldContext(firstFieldSchema);
      // Pass a wrapper function that will call the latest handleTextSubmit via ref
      onActiveFormChange(true, (text: string) => handleTextSubmitRef.current?.(text), fieldContext);
    }
  }, [messages, activeForm, onActiveFormChange]);

  // Helper to generate field context (placeholder, type)
  const getFieldContext = (fieldSchema: RJSFSchema): FieldContext => {
    const fieldType = fieldSchema.type as FieldType;
    const description = fieldSchema.description as string | undefined;

    // For text/number/email fields, generate chat input placeholder
    if (fieldType === "string" && !Array.isArray(fieldSchema.enum)) {
      const format = (fieldSchema as { format?: string }).format;
      if (format === "email") {
        return { placeholder: `Enter your email address...`, fieldType: "email" };
      }
      return { placeholder: description || `Type your answer...`, fieldType: "text" };
    }

    if (fieldType === "number" || fieldType === "integer") {
      return { placeholder: description || `Enter a number...`, fieldType: "number" };
    }

    // For radio/checkboxes, generic placeholder
    return { placeholder: "Type your answer or select above...", fieldType: "choice" };
  };

  // Helper to determine if field widget should be hidden (use chat input only)
  const shouldHideFieldWidget = (fieldSchema: RJSFSchema): boolean => {
    const fieldType = fieldSchema.type as FieldType;

    // Hide widget for simple text/number/email inputs (user types in chat input)
    if (fieldType === "string" && !Array.isArray(fieldSchema.enum)) {
      return true; // text, email fields
    }
    if (fieldType === "number" || fieldType === "integer") {
      return true; // number fields
    }

    return false; // radio, checkboxes, arrays — show widget
  };

  const handleFieldSubmit = useCallback(
    async (fieldName: string, value: unknown) => {
      if (!activeForm) return;

      const newAnswers = { ...fieldAnswers, [fieldName]: value };
      setFieldAnswers(newAnswers);
      setTextInputError(null);

      const fieldKeys = fieldKeysRef.current;
      const nextIndex = currentFieldIndex + 1;

      if (nextIndex >= fieldKeys.length) {
        // Last field — submit entire form
        await addToolOutput({
          tool: "show_form",
          toolCallId: activeForm.toolCallId,
          output: { formData: newAnswers },
        });
        // Reset state
        setActiveForm(null);
        setCurrentFieldIndex(0);
        setFieldAnswers({});
        fieldKeysRef.current = [];
        onActiveFormChange(false);
      } else {
        // Advance to next field
        setCurrentFieldIndex(nextIndex);

        // Update field context for next field
        const properties = activeForm.schema.properties as Record<string, RJSFSchema>;
        const nextFieldKey = fieldKeys[nextIndex];
        const nextFieldSchema = properties[nextFieldKey];
        const fieldContext = getFieldContext(nextFieldSchema);
        // Pass a wrapper function that will call the latest handleTextSubmit via ref
        onActiveFormChange(
          true,
          (text: string) => handleTextSubmitRef.current?.(text),
          fieldContext,
        );
      }
    },
    [activeForm, fieldAnswers, currentFieldIndex, addToolOutput, onActiveFormChange],
  );

  const handleBack = useCallback(() => {
    if (currentFieldIndex > 0) {
      setCurrentFieldIndex(currentFieldIndex - 1);
      setTextInputError(null);
    }
  }, [currentFieldIndex]);

  const handleCurrentDataChange = useCallback((fieldName: string, value: unknown) => {
    // Store current field data for submission
    currentFieldDataRef.current = { fieldName, value };
    setTextInputError(null);
  }, []);

  const handleTextSubmit = useCallback(
    (text: string) => {
      if (!activeForm) return;

      const fieldKeys = fieldKeysRef.current;
      const currentFieldKey = fieldKeys[currentFieldIndex];

      let value: unknown;

      // If text is provided, parse it. Otherwise, use current form widget value or existing answer
      if (text.trim()) {
        value = text;
      } else {
        // No text provided, check current widget value or previously saved answer
        if (currentFieldDataRef.current?.fieldName === currentFieldKey) {
          value = currentFieldDataRef.current.value;
        } else if (fieldAnswers[currentFieldKey] !== undefined) {
          // Use existing answer (important when going back to a previous step)
          value = fieldAnswers[currentFieldKey];
        } else {
          return; // No value to submit
        }
      }

      // Submit field without validation
      handleFieldSubmit(currentFieldKey, value);
    },
    [activeForm, currentFieldIndex, handleFieldSubmit, fieldAnswers],
  );

  // Keep ref updated with latest handleTextSubmit
  useEffect(() => {
    handleTextSubmitRef.current = handleTextSubmit;
  }, [handleTextSubmit]);

  if (!activeForm) {
    return null;
  }

  const fieldKeys = fieldKeysRef.current;
  const properties = activeForm.schema.properties as Record<string, RJSFSchema>;
  const currentFieldKey = fieldKeys[currentFieldIndex];
  const currentFieldSchema = properties[currentFieldKey];

  return (
    <div>
      <StepperJsonForm
        schema={activeForm.schema}
        uiSchema={activeForm.uiSchema}
        currentFieldIndex={currentFieldIndex}
        totalFields={fieldKeys.length}
        fieldAnswers={fieldAnswers}
        onFieldSubmit={handleFieldSubmit}
        onBack={handleBack}
        canGoBack={currentFieldIndex > 0}
        onCurrentDataChange={handleCurrentDataChange}
        hideWidget={shouldHideFieldWidget(currentFieldSchema)}
      />

      {textInputError && (
        <div className="mt-2 px-4 py-2 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
          {textInputError}
        </div>
      )}
    </div>
  );
}
